'use strict';
import { Matrix4 } from 'three/src/math/Matrix4';
import { Vector3 } from 'three/src/math/Vector3';
import { PerspectiveCamera } from 'three/src/cameras/PerspectiveCamera';
import { Scene } from 'three/src/scenes/Scene';
import { MeshBasicMaterial } from 'three/src/materials/MeshBasicMaterial';
import { DoubleSide, LinearFilter } from 'three/src/constants';
import { SphereGeometry } from 'three/src/geometries/SphereGeometry';
import { Mesh } from 'three/src/objects/Mesh';
import { WebGLRenderer } from 'three/src/renderers/WebGLRenderer';
import { Raycaster } from 'three/src/core/Raycaster';
import { TextureLoader } from 'three/src/loaders/TextureLoader';
import { CanvasTexture } from 'three/src/textures/CanvasTexture';

import { OrbitControls } from './OrbitControls';
export default class ShericProjector {
  constructor(elt, config) {
    const defaults = {
      minFov: 30,
      maxFov: 110,
      zoomSensibility: 0.05,
      dampingFactor: 0.1,
      rotateSpeed: 0.4,
    };
    Object.assign(this, defaults, config);
    this.hotspots = [];
    this.matrixConversion = new Matrix4();
    this._touchZoomDistanceStart = 0;
    this.halfWidth = 0;
    this.halfHeight = 0;
    this.editMode = config?.editMode;
    this.initCamera();
    this.initScene();
    this.initRenderer();
    this.createControls();
    this.initHandlers();
    this.animate();
    elt && this.initTexture(elt);
  }
  setOnChangeHandler(handler) {
    this.onChange = handler;
  }
  initCamera() {
    this.camera = new PerspectiveCamera(75, 1, 1, 1100);
    this.camera.position.set(0, 0, 1);
  }
  initScene() {
    this.scene = new Scene();
    this.material = new MeshBasicMaterial();
    this.material.side = DoubleSide;
    this.geometry = new SphereGeometry(1000, 128, 128);
    this.sphere = new Mesh(this.geometry, this.material);
    this.sphere.scale.x = -1;
    this.scene.add(this.sphere);
  }
  initTexture(elt, cb) {
    // l'élément peut être de 2 types : soit un canvas, soit une url d'image
    if (typeof elt === 'string') {
      const loader = new TextureLoader();
      this.texture = loader.load(elt, () => {
        this.applyTexture();
        cb && cb();
        this.render();
      });
    } else {
      this.texture = new CanvasTexture(elt);
      this.applyTexture();
    }
  }
  applyTexture() {
    this.texture.generateMipmaps = false;
    this.texture.needsUpdate = true;
    this.texture.magFilter = LinearFilter;
    this.texture.minFilter = LinearFilter;
    this.material.map = this.texture;
    this.render();
  }
  initRenderer() {
    this.renderer = new WebGLRenderer({ alpha: true,preserveDrawingBuffer: true });
    this.renderer.setPixelRatio(1);
    this.renderer.setClearColor(0xfcfcfc);
    //this.renderer.setPixelRatio(window.devicePixelRatio);
  }
  resize(size, noRender) {
    this.renderer.setSize(size.width, size.height);
    this.camera.aspect = size.width / size.height;
    this.halfWidth = size.width / 2;
    this.halfHeight = size.height / 2;
    this.camera.updateProjectionMatrix();
    if (noRender) {
      return;
    }
    this.render();
  }
  needsUpdate() {
    this.texture.needsUpdate = true;
  }
  getDomElement() {
    return this.renderer.domElement;
  }
  /*setHotspots(hotspots) {
    let hotspotdata;
    this.hotspots = [];
    for (let hotspot of hotspots) {
      hotspotdata = angular.element(hotspot).scope().hotspot;
      this.hotspots.push({
        elt: hotspot,
        data:hotspotdata
      });
    }
  }*/

  autoRotate(speed) {
    if (speed === -1) {
      this.controls.autoRotate = false;
      return;
    }
    this.controls.autoRotateSpeed = speed;
    this.controls.autoRotate = true;
  }

  clearHotspots() {
    this.hotspots = [];
  }
  addHotspot(elt, data) {
    this.hotspots.push({
      elt,
      data,
    });
  }

  updateHotspots() {
    let hotspotposition;
    this.camera.updateMatrix();
    this.camera.updateMatrixWorld();
    this.matrixConversion.copy(this.camera.matrixWorld).invert();
    this.hotspots.forEach((h) => {
      hotspotposition = this.convertWorldToPage({
        x: h.data.x,
        y: h.data.y,
        z: h.data.z,
      });
      let x = hotspotposition.x - h.elt.offsetWidth / 2;
      if (this.editMode) {
        if (h.data.ismoving) {
          return;
        }
        h.elt.style.left = `${x}px`;
        h.elt.style.top = `${hotspotposition.y}px`;
      } else {
        h.elt.style.transform = `translate(${x}px,${hotspotposition.y}px)`;
      }
    });
  }

  setFov(fov) {
    this.camera.fov = Math.max(this.minFov, Math.min(this.maxFov, fov));
    this.camera.updateProjectionMatrix();
  }

  render() {
    this.renderer.render(this.scene, this.camera);
    if (this.onChange) {
      const longLat = this.getLongLat();
      const position = {
        long: longLat[0],
        lat: longLat[1],
        fov: this.camera.fov,
      };
      this.onChange(position);
    }
  }

  animate() {
    this.animateSphere();
    this.animateHotspots();
  }
  animateSphere() {
    if (this.animationCanceled) return;
    requestAnimationFrame(this.animateSphere.bind(this));
    this.controls.update();
  }
  animateHotspots() {
    if (this.animationCanceled) return;
    requestAnimationFrame(this.animateHotspots.bind(this));
    this.updateHotspots();
  }
  createControls() {
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.noPan = true;
    this.controls.noZoom = true;
    this.controls.enableDamping = true;
    this.controls.dampingFactor = this.dampingFactor;
    this.controls.rotateSpeed = this.rotateSpeed;
    //this.controls.maxPolarAngle = Math.PI / 2;
    this.controls.addEventListener('change', this.render.bind(this));
  }
  onMouseWheel(event) {
    event.stopPropagation();
    event.preventDefault();
    let fov = this.camera.fov;
    if (event.wheelDeltaY) {
      // WebKit
      fov -= event.wheelDeltaY * this.zoomSensibility;
    } else if (event.wheelDelta) {
      // Opera / IE9
      fov -= event.wheelDelta * this.zoomSensibility;
    } else if (event.detail) {
      // Firefox
      fov += event.detail * 1.0;
    }
    this.setFov(fov);
    this.render();
  }
  onTouch(event) {
    event.preventDefault();
    if (event.touches.length == 2) {
      event.stopPropagation();
      var dx = event.touches[0].pageX - event.touches[1].pageX;
      var dy = event.touches[0].pageY - event.touches[1].pageY;
      if (!this._touchZoomDistanceStart) {
        this._touchZoomDistanceStart = Math.sqrt(dx * dx + dy * dy);
      } else {
        let _touchZoomDistanceEnd = Math.sqrt(dx * dx + dy * dy);
        const factor = this._touchZoomDistanceStart / _touchZoomDistanceEnd;
        this._touchZoomDistanceStart = _touchZoomDistanceEnd;
        this.setFov(this.camera.fov * factor);
        this.render();
      }
    }
  }
  onTouchEnd() {
    this._touchZoomDistanceStart = 0;
  }
  initHandlers() {
    this.handlers = {
      mousewheel: this.onMouseWheel.bind(this),
      DOMMouseScroll: this.onMouseWheel.bind(this),
      touchstart: this.onTouch.bind(this),
      touchmove: this.onTouch.bind(this),
      touchend: this.onTouchEnd.bind(this),
      touchleave: this.onTouchEnd.bind(this),
    };
    Object.keys(this.handlers).forEach((evt) => {
      this.renderer.domElement.addEventListener(evt, this.handlers[evt], false);
    });
  }
  removeHandlers() {
    Object.keys(this.handlers).forEach((evt) => {
      this.renderer.domElement.removeEventListener(evt, this.handlers[evt], false);
    });
  }
  initPosition(position) {
    console.log('initPosition', position);
    this.setLongLat(position.long, position.lat);
    this.setFov(position.fov);
  }
  setLongLat(long, lat) {
    console.log('setLongLat', long, lat);
    var point = new Vector3();
    lat = Math.PI / 2 - lat;
    point.setX(Math.sin(lat) * Math.sin(long));
    point.setY(Math.cos(lat));
    point.setZ(Math.sin(lat) * Math.cos(long));
    point.normalize().negate();
    this.camera.position.copy(point);
  }
  getLongLat() {
    var point = new Vector3();
    point.copy(this.camera.position);
    point.normalize().negate();
    var lng = -Math.atan2(-point.z, -point.x) - Math.PI / 2;
    if (lng < -Math.PI) lng += Math.PI * 2;
    var p = new Vector3(point.x, 0, point.z);
    p.normalize();
    var lat = Math.acos(p.dot(point));
    if (point.y < 0) lat *= -1;
    return [lng, lat];
  }
  convertWorldToPage(point) {
    let p = new Vector3(point.x, point.y, point.z);
    p.applyMatrix4(this.matrixConversion);
    if (p.z > 0) {
      return { x: -9999, y: -9999 };
    }
    p = new Vector3(point.x, point.y, point.z);
    const position2 = p.project(this.camera);
    position2.x = (1 + position2.x) * this.halfWidth;
    position2.y = (1 - position2.y) * this.halfHeight;
    return position2;
  }
  convertPageToWorld(coords2d) {
    const renderer = this.renderer;
    const boundingRect = renderer.domElement.getBoundingClientRect();
    const width = renderer.domElement.width;
    const height = renderer.domElement.height;
    let x = (coords2d.x - boundingRect.left) * (width / boundingRect.width);
    let y = (coords2d.y - boundingRect.top) * (height / boundingRect.height);
    x = (x / width) * 2 - 1;
    y = -(y / height) * 2 + 1;
    let vector = new Vector3(x, y, 0.5);
    let raycaster = new Raycaster();
    raycaster.setFromCamera(vector.clone(), this.camera);
    let intersects = raycaster.intersectObjects(this.scene.children, true);
    return intersects;
  }
  destroy() {
    this.animationCanceled = true;
    this.hotspots = null;
    this.removeHandlers();
    this.texture.dispose();
    this.texture = null;
    this.geometry.dispose();
    this.material.dispose();
    this.controls.dispose();
    this.scene.dispose();
    this.renderer.dispose();
    this.renderer = null;
  }
}
